// CookDonenessGate — pre-cook questionnaire for thickness-driven
// proteins. Shown on the CookMode overview before START COOKING when the
// recipe contains a steak / chop / fillet (see src/lib/doneness.js). The
// cook sets thickness + target doneness per protein; we surface the
// target internal temp (the truth) and a thickness-scaled time estimate,
// and CookMode bakes both into the relevant cook step.
//
// Temp is the headline, time is "roughly N min/side, check the
// thermometer." We never present the clock as authoritative.

import React from "react";
import { FONT, COLOR } from "../lib/tokens";
import { getMeasurementSystem, useUnitPrefsVersion } from "../lib/unitPrefs";
import {
  donenessOptionsFor,
  donenessGuidance,
  defaultThicknessIn,
} from "../lib/doneness";

const MIN_IN = 0.25;
const MAX_IN = 2.5;

function clampIn(v) {
  return Math.min(MAX_IN, Math.max(MIN_IN, v));
}

function ThicknessStepper({ thicknessIn, metric, onChange }) {
  const step = metric ? 0.5 : 0.25;       // cm vs inch increments
  const display = metric ? thicknessIn * 2.54 : thicknessIn;
  const unit = metric ? "cm" : "in";
  const shown = metric ? display.toFixed(1) : (display % 1 === 0 ? String(display) : display.toFixed(2));
  const bump = (dir) => {
    const nextDisplay = display + dir * step;
    const nextIn = clampIn(metric ? nextDisplay / 2.54 : nextDisplay);
    onChange(nextIn);
  };
  const btn = {
    width: 34, height: 34, borderRadius: 17, flexShrink: 0,
    background: COLOR.soil, border: `1px solid ${COLOR.border}`,
    color: COLOR.gold, fontSize: 18, fontWeight: 600, lineHeight: 1,
    padding: 0, cursor: "pointer",
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <button onClick={() => bump(-1)} aria-label="Thinner" style={btn}>−</button>
      <div style={{ minWidth: 78, textAlign: "center" }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 18, fontWeight: 600, color: COLOR.ink }}>{shown}</span>
        <span style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim, marginLeft: 4 }}>{unit}</span>
      </div>
      <button onClick={() => bump(1)} aria-label="Thicker" style={btn}>+</button>
    </div>
  );
}

function ProteinCard({ protein, answer, onChange }) {
  const opts = donenessOptionsFor(protein.canonicalId);
  const metric = getMeasurementSystem() === "metric";
  const thicknessIn = answer?.thicknessIn ?? defaultThicknessIn(protein.canonicalId);
  const donenessId = answer?.donenessId ?? (opts.find((o) => o.recommended) || opts[0])?.id;
  const g = donenessGuidance({ canonicalId: protein.canonicalId, thicknessIn, donenessId });

  const set = (patch) => onChange(protein.canonicalId, { thicknessIn, donenessId, ...patch });

  return (
    <div style={{
      background: COLOR.soil, border: `1px solid ${COLOR.border}`,
      borderRadius: 12, padding: "14px 16px", marginBottom: 10,
    }}>
      <div style={{
        fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
        fontSize: 17, color: COLOR.ink, marginBottom: 12,
      }}>
        {protein.name}
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, marginBottom: 14,
      }}>
        <span style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim, letterSpacing: "0.06em" }}>
          THICKNESS
        </span>
        <ThicknessStepper
          thicknessIn={thicknessIn}
          metric={metric}
          onChange={(v) => set({ thicknessIn: v })}
        />
      </div>

      {opts.length > 1 && (
        <>
          <div style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim, letterSpacing: "0.06em", marginBottom: 8 }}>
            HOW DO YOU LIKE IT
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
            {opts.map((o) => {
              const active = o.id === donenessId;
              return (
                <button
                  key={o.id}
                  className="mcm-focusable"
                  onClick={() => set({ donenessId: o.id })}
                  style={{
                    padding: "7px 12px", borderRadius: 999,
                    background: active ? COLOR.leafDeep : COLOR.ground,
                    border: `1px solid ${active ? COLOR.leaf : COLOR.edge}`,
                    color: active ? COLOR.leaf : COLOR.dim,
                    fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                    cursor: "pointer", touchAction: "manipulation",
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Result — temp is the headline (green = the goal), time is the estimate. */}
      {g && g.patty && (
        <div style={{
          background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
          borderRadius: 10, padding: "10px 12px",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: FONT.mono, fontSize: 18, fontWeight: 700, color: COLOR.leaf }}>
              {g.doneness.finalF}°F
            </span>
            <span style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim }}>
              cooked through (food-safe)
            </span>
          </div>
          <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, marginTop: 4 }}>
            Estimate {g.timeEstimateLabel} in a hot pan — go by internal temp, not the buzzer.
          </div>
          <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim, marginTop: 6 }}>
            Ground meat is always cooked fully — no rare/medium, for safety.
          </div>
        </div>
      )}
      {g && !g.patty && (
        <div style={{
          background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
          borderRadius: 10, padding: "10px 12px",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontFamily: FONT.mono, fontSize: 18, fontWeight: 700, color: COLOR.leaf }}>
              {g.doneness.pullF}°F
            </span>
            <span style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim }}>
              pull temp → {g.doneness.finalF}°F after rest
            </span>
          </div>
          <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, marginTop: 4 }}>
            {g.family === "shellfish"
              ? `Estimate ${g.timeEstimateLabel.replace("per side", "total")} — go by opacity, not the clock.`
              : g.reverseSear
                ? `Thick cut — reverse-sear, then check the thermometer (sear est. ${g.timeEstimateLabel}).`
                : `Estimate ${g.timeEstimateLabel} in a hot pan — pull at temp, not on the buzzer.`}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<{canonicalId:string,name:string}>} props.proteins
 * @param {Object.<string,{thicknessIn:number,donenessId:string}>} props.answers
 * @param {(canonicalId:string, answer:object)=>void} props.onChange
 */
export default function CookDonenessGate({ proteins = [], answers = {}, onChange }) {
  // Re-render when the US ↔ metric preference flips so thickness units track.
  useUnitPrefsVersion();
  if (!proteins.length) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 12, color: COLOR.gold,
        letterSpacing: "0.12em", marginBottom: 6,
      }}>
        COOK IT RIGHT
      </div>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim,
        lineHeight: 1.45, marginBottom: 12,
      }}>
        Tell me how thick it is and how you like it — I'll set the timers and the target temp to match.
      </div>
      {proteins.map((p) => (
        <ProteinCard
          key={p.canonicalId}
          protein={p}
          answer={answers[p.canonicalId]}
          onChange={onChange}
        />
      ))}
    </div>
  );
}
